// 6-card detail header: Symbol/Position/Net/Realized/Unrealized/Cycles.
// Mirrors legacy renderWindDetailStats. Live Net + Unrealized update via WS ticks.
import type { WindDetailData } from "@/hooks/use-wind-detail";
import { useWindLivePnl } from "@/hooks/use-wind-live-pnl";
import { fmt, pnlTone } from "@/lib/format";
import StatCard from "@/components/common/stat-card";
import SessionDirectionBadge from "@/components/wind/session-direction-badge";

interface WindStatsProps {
  data: WindDetailData;
}

export default function WindStats({ data }: WindStatsProps) {
  const { session, latest_cycle, summary, cycles } = data;

  const dir = latest_cycle?.direction ?? session.direction_initial ?? "long";
  const cycleStatus = latest_cycle?.status ?? null;

  // Backend already computes pnl = gross - fees per trade, so summary.total_pnl
  // is already net of fees. Do NOT subtract total_fees again — that would
  // double-deduct fees and make NET < Realized PnL with no unrealized component.
  const realizedNet = summary.total_pnl ?? 0;

  const { livePrice, unrealized, net } = useWindLivePnl(
    session,
    latest_cycle,
    realizedNet
  );

  const winRate =
    summary.trade_count > 0
      ? (summary.win_count / summary.trade_count) * 100
      : 0;
  const wrColor =
    winRate >= 60 ? "#22c55e" : winRate >= 40 ? "#eab308" : "#ef4444";

  // Cycles stat sub-text: session status badge + cycle phase
  const sessionStatus = session.status;
  let cyclePhaseLabel = "";
  if (sessionStatus === "active" && cycleStatus) {
    const chasing =
      cycleStatus === "pending" && latest_cycle?.entry_limit_price;
    const label = chasing ? "chasing maker" : cycleStatus;
    cyclePhaseLabel = `cycle ${cycles.length} ${label}`;
  }

  const tone = (v: number) => pnlTone(v);

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mb-4">
      {/* Symbol — min-w-0 + break-all so long symbol + price don't overflow
          the narrow mobile grid cell (half-screen on 2-col layout). Price
          wraps to its own line rather than truncating mid-digit. */}
      <div className="bg-bg border border-border rounded-lg p-3 min-w-0 overflow-hidden">
        <div className="text-muted text-[0.7rem] uppercase tracking-widest">
          Symbol
        </div>
        {/* Symbol + price layout is responsive:
            - mobile (base, 2-col grid): price on its own line (3 lines total)
              since the card is ~180px wide and inline would wrap mid-digit
            - sm+ (3-col grid): card is wider, inline the price after symbol
              (2 lines total) for a denser look. break-all still protects
              against extreme-long symbols. */}
        <div className="text-sm font-semibold mt-0.5 text-text break-all">
          {session.symbol}
          {livePrice != null && (
            <span className="text-warn text-xs font-normal hidden sm:inline sm:ml-1">
              {fmt(livePrice, session.symbol)}
            </span>
          )}
        </div>
        {livePrice != null && (
          <div className="text-warn text-xs font-normal mt-0.5 break-all sm:hidden">
            {fmt(livePrice, session.symbol)}
          </div>
        )}
        <div className="text-xs mt-0.5 flex items-center gap-1 flex-wrap">
          <SessionDirectionBadge
            direction={dir}
            cycleStatus={cycleStatus}
            cooldownUntil={latest_cycle?.cooldown_until}
            closeReason={latest_cycle?.close_reason}
            pausedUntil={session.paused_until}
          />
          <span className="font-semibold" style={{ color: "#a855f7" }}>
            {session.leverage}x
          </span>
        </div>
      </div>

      {/* Position */}
      <StatCard
        label="Position"
        value={fmt(latest_cycle?.quantity ?? 0, 8)}
        sub={session.margin ? `$${fmt(session.margin, 2)} margin` : undefined}
        title="Position size on the current cycle"
      />

      {/* Net */}
      <div
        className="bg-bg border border-border rounded-lg p-3 min-w-0"
        title="Net = Realized PnL + unrealized on open cycle"
      >
        <div className="text-muted text-[0.7rem] uppercase tracking-widest">
          Net
        </div>
        <div
          id="wind-live-net"
          className={[
            "text-lg font-semibold mt-0.5 tabular-nums transition-colors duration-300",
            net >= 0 ? "text-success" : "text-danger",
          ].join(" ")}
        >
          {net >= 0 ? "+" : ""}
          {fmt(net, 4)}
        </div>
        {summary.trade_count > 0 && (
          <div className="text-xs mt-0.5">
            <span style={{ color: wrColor }}>
              {summary.win_count}/{summary.trade_count}
            </span>
            <span className="text-muted"> ({winRate.toFixed(1)}%)</span>
          </div>
        )}
      </div>

      {/* Realized PnL */}
      <StatCard
        label="Realized PnL"
        value={fmt(summary.total_pnl ?? 0, 4)}
        sub="net of fees"
        tone={tone(summary.total_pnl ?? 0)}
        title="Realized PnL from all closed cycles in this session, net of fees"
      />

      {/* Unrealized PnL */}
      <div
        className="bg-bg border border-border rounded-lg p-3 min-w-0"
        title="Mark-to-market on the currently-open cycle, incl. opening fee and estimated taker close fee"
      >
        <div className="text-muted text-[0.7rem] uppercase tracking-widest">
          Unrealized PnL
        </div>
        <div
          id="wind-live-unrealized"
          className={[
            "text-lg font-semibold mt-0.5 tabular-nums transition-colors duration-300",
            unrealized >= 0 ? "text-success" : "text-danger",
          ].join(" ")}
        >
          {fmt(unrealized, 4)}
        </div>
        <div className="text-muted text-xs mt-0.5">incl. fees</div>
      </div>

      {/* Cycles */}
      <div
        className="bg-bg border border-border rounded-lg p-3 min-w-0"
        title="Session lifecycle + current cycle phase"
      >
        <div className="text-muted text-[0.7rem] uppercase tracking-widest">
          Cycles
        </div>
        <div className="text-lg font-semibold mt-0.5 text-text tabular-nums">
          {cycles.length}
          <span className="text-muted text-xs font-normal"> total</span>
        </div>
        <div className="text-xs mt-0.5 flex items-center gap-1 flex-wrap">
          <span
            className={`text-[0.65rem] px-1 py-0.5 rounded capitalize ${
              sessionStatus === "active"
                ? "bg-success/20 text-success"
                : "bg-muted/20 text-muted"
            }`}
          >
            {sessionStatus}
          </span>
          {cyclePhaseLabel && (
            <span className="text-muted text-[0.65rem]">{cyclePhaseLabel}</span>
          )}
        </div>
      </div>
    </div>
  );
}
